/* eslint-disable @typescript-eslint/no-use-before-define, no-use-before-define */
import {
  Autocomplete,
  Box,
  CircularProgress,
  Divider,
  Drawer,
  Hidden,
  List,
  ListItem,
  MenuItem,
  TextField,
} from '@mui/material';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { matchPath, useLocation } from 'react-router';

import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material/';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  setAccountFarm,
  setAccountFarmsOptions,
  setSelectedFarmSectors,
} from '../../../actions/accountFarmAction';
import { logout } from '../../../actions/accountActions';
import { setCurrentSector } from '../../../actions/currentSectorActions';
import { setUserAccount, setLocalAccounts } from '../../../actions/userAccountAction';
import { resetGraphFilters } from '../../../actions/graphsFilterActions';
import { UserRole, UserType } from '../../../constants/user_types';
import apiClient from '../../../requests/api/apiClient';
import {
  AccountSchema, FarmSchema, SectorSchema, NodeSchema, SensorModelSchema,
} from '../../../requests/api/apiTypes';
import { NavigationItemProps, ReduxState } from '../../../types';
import NavItem from './NavItem';
import useStyles from './styles';
import { getUpdatedSpecificNavConfig, navConfig, filterNavItemsByRole } from './utils';
import { setAllFarmDevices, setDashboardType } from '../../../actions/farmSectorActions';
import { setApiCache } from '../../../actions/apiCacheAction';
import useSensorModels from '../../../hooks/useSensorModels';
import useNodes from '../../../hooks/useNodes';
import { clearPersistedStorage } from '../../../store/persistor';

const USER_ACCOUNTS_CACHE_TTL_MS = 5 * 60 * 1000;

interface UserAccountsResponse {
  userAccounts?: AccountSchema[];
}

function renderNavItems(
  items: NavigationItemProps[],
  pathname: string,
  userType: UserType,
  depth = 0,
): JSX.Element {
  return (
    <List disablePadding>
      {items?.reduce(
        (acc: JSX.Element[], item) => reduceChildRoutes(acc, pathname, item, userType, depth),
        [],
      )}
    </List>
  );
}

function reduceChildRoutes(
  acc: JSX.Element[],
  pathname: string,
  item: NavigationItemProps,
  userType: UserType,
  depth = 0,
) {
  const key = `${item.title || ''}depth`;

  if (item.userTypesAllowed?.includes(userType)) {
    if (item.items && item.items.length > 0) {
      const open = matchPath(pathname, {
        path: item.href,
        exact: false,
      }) || item.items.some((subItem) => pathname.includes(subItem.href || ''));
      acc.push(
        <NavItem
          depth={depth}
          icon={item.icon}
          key={key}
          info={item.info}
          open={Boolean(open)}
          title={item.title}
          href={item.href}
          items={item.items}
        >
          {renderNavItems(item.items as NavigationItemProps[], pathname, userType, depth + 1)}
        </NavItem>,
      );
    } else {
      acc.push(
        <NavItem
          depth={depth}
          href={item.href}
          icon={item.icon}
          key={key}
          info={item.info}
          title={item.title}
        />,
      );
    }
  }
  return acc;
}

function NavBar({
  openMobile, onMobileClose, openDrawer, onDrawerAction,
}: {
  openMobile: boolean, onMobileClose: () => void,
  openDrawer: boolean, onDrawerAction: () => void
}) {
  const classes = useStyles();
  const location = useLocation();
  const dispatch = useDispatch();
  const { user } = useSelector((state: ReduxState) => state.account);
  const { selectedAccount } = useSelector((state: ReduxState) => state.userAccount);
  const { selectedFarm, farmsOptions } = useSelector((state: ReduxState) => state.accountFarm);
  const [sectorItems, setSectorItems] = useState<NavigationItemProps[]>([]);
  const { localAccounts } = useSelector((state: ReduxState) => state.localAccounts);
  const [isLoadingSectors, setIsLoadingSectors] = useState(false);
  const navigate = useHistory();

  const { nodes } = useNodes(selectedFarm?.id ?? '');
  const { sensorModels, isLoaded: sensorModelsLoaded } = useSensorModels(
    selectedFarm?.id ?? '',
  );

  const userAccountsCacheKey = `userAccounts:detail:${user?.id ?? ''}`;
  const cachedUserAccounts = useSelector(
    (state: ReduxState) => state.apiCache?.entries?.[userAccountsCacheKey],
  );
  const cachedUserAccountsRef = useRef(cachedUserAccounts);
  cachedUserAccountsRef.current = cachedUserAccounts;

  useEffect(() => {
    let isMounted = true;

    const applyData = (data: UserAccountsResponse | undefined) => {
      if (!data?.userAccounts) return;
      if (data.userAccounts.length >= 1 && !selectedAccount?.id) {
        handleAccountSelection(data.userAccounts[0]);
      }
      dispatch(setLocalAccounts(data.userAccounts as AccountSchema[]));
    };

    const cached = cachedUserAccountsRef.current;
    const fresh = !!cached && Date.now() - cached.timestamp < USER_ACCOUNTS_CACHE_TTL_MS;
    if (fresh && cached) {
      applyData(cached.data as UserAccountsResponse);
    }

    const fetchData = async () => {
      try {
        const { data } = await apiClient.userAccounts.userAccountsDetail(user?.id);
        if (!isMounted) return;
        applyData(data as UserAccountsResponse);
        dispatch(setApiCache(userAccountsCacheKey, data));
      } catch (error) {
        if (!isMounted || (error as Response)?.status !== 401) return;
        await dispatch(logout());
        await clearPersistedStorage();
        navigate.push('/login');
      }
    };
    fetchData();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    // Update farm devices whenever nodes or sensorModels change
    if (selectedFarm?.id) {
      const allDevices = [...nodes, ...sensorModels];
      dispatch(setAllFarmDevices(allDevices));
      const uniqueSectors = [...new Map(
        (selectedFarm?.sectors ?? []).map((s) => [s.id, s]),
      ).values()].sort((a, b) => (a.name ?? '')
        .localeCompare(b.name ?? ''));
      updateSectorItems(
        uniqueSectors,
        allDevices,
        sensorModelsLoaded,
      );
    } else {
      // No farm selected — still render base nav items (Inicio, Monitoreo, etc.)
      updateSectorItems([], [], sensorModelsLoaded);
    }
  }, [nodes, sensorModels, selectedFarm?.id,
    selectedFarm?.sectors, sensorModelsLoaded]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const farmId = searchParams.get('farmId');
    const sectorId = searchParams.get('sectorId');

    if (farmId && localAccounts && localAccounts.length > 0) {
      setCurrentFarmAndSector(localAccounts, farmId, sectorId || undefined);
    }
  }, [location.search, localAccounts]);

  useEffect(() => {
    const farmId = selectedFarm?.id;
    if (!farmId) return undefined;

    let isMounted = true;

    const fetchFarmSectors = async () => {
      try {
        const { data } = await apiClient.sectors.sectorsDetail(farmId);
        if (!isMounted) return;
        const sectors = (data?.sectors ?? []) as SectorSchema[];
        dispatch(setSelectedFarmSectors(sectors));
      } catch (error) {
        // Silent fail; sectors stay with previous value until next farm change
      }
    };
    fetchFarmSectors();

    return () => {
      isMounted = false;
    };
  }, [selectedFarm?.id]);

  const setCurrentFarmAndSector = async (accounts: AccountSchema[],
    farmId: string, sectorId?: string) => {
    const accountWithFarm = accounts.find(
      (account) => account.farms?.some((f) => f.id === farmId),
    );

    if (accountWithFarm) {
      dispatch(setUserAccount(accountWithFarm));
      dispatch(setAccountFarmsOptions(accountWithFarm.farms || []));

      const foundFarm = accountWithFarm.farms?.find((f) => f.id === farmId);

      const shouldUpdateFarm = selectedAccount?.id !== accountWithFarm.id
        || selectedFarm?.id !== foundFarm?.id;
      if (shouldUpdateFarm) {
        if (foundFarm) {
          handleAccountSelection(accountWithFarm, foundFarm);
        } else {
          handleAccountSelection(accountWithFarm);
        }
      }

      if (sectorId) {
        // Fetch complete sector data from backend to ensure all fields
        try {
          const { data } = await apiClient.sector.sectorDetail(sectorId);
          if (data?.sector) {
            dispatch(setCurrentSector(data.sector as SectorSchema));
          }
        } catch (error) {
          console.error('Error fetching sector details:', error);
          // Fallback to local data if fetch fails
          const sector = foundFarm?.sectors?.find((s) => s.id === sectorId);
          if (sector) {
            dispatch(setCurrentSector(sector as SectorSchema));
          }
        }
      }
    }
  };

  useEffect(() => {
    if (openMobile && onMobileClose) {
      onMobileClose();
    }
    // eslint-disable-next-line
  }, [location.pathname]);

  const getEmptyFarm = (): FarmSchema => ({
    id: '',
    name: '',
    center: {
      lat: '',
      lng: '',
    },
    location: '',
    theoreticalSurface: '',
  });

  const updateSectorItems = async (
    sectors: SectorSchema[] = [],
    devices: Array<NodeSchema | SensorModelSchema> = [],
    devicesLoaded = true,
  ) => {
    setIsLoadingSectors(true);
    setSectorItems([]);
    // filter sectors repeated by id
    const uniqueSectors = sectors.filter(
      (sector, index, self) => index === self.findIndex((t) => t.id === sector.id),
    );
    const updatedNavConfig = getUpdatedSpecificNavConfig(
      uniqueSectors, devices, user?.type as UserType,
      user?.role as UserRole, devicesLoaded, selectedFarm?.id,
    );
    setSectorItems(updatedNavConfig);
    setIsLoadingSectors(false);
  };

  const handleAccountSelection = (account: AccountSchema, farm?: FarmSchema) => {
    const farmSelected = farm || account?.farms?.[0] || getEmptyFarm();
    dispatch(setUserAccount(account));
    dispatch(setAccountFarmsOptions(account.farms || []));
    dispatch(resetGraphFilters());
    dispatch(setDashboardType(1));
    if (account.farms && account.farms.length > 0) {
      handleFarmSelection(farmSelected);
    } else {
      handleFarmSelection(getEmptyFarm());
    }
  };

  const handleFarmSelection = (farm: FarmSchema) => {
    dispatch(setAccountFarm(farm || getEmptyFarm()));
    dispatch(setCurrentSector(null));
    dispatch(resetGraphFilters());
    dispatch(setDashboardType(1));
  };

  const onAccountChange = (_event: React.SyntheticEvent, newValue: AccountSchema | null) => {
    if (newValue) {
      handleAccountSelection(newValue);
      navigate.push('/inicio');
    }
  };

  const onFarmChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    if (farmsOptions && e.target.value !== '0') {
      const auxFarm = farmsOptions.find((elem) => elem.id === e.target.value);
      if (auxFarm) {
        handleFarmSelection(auxFarm);
      }
    }

    if (!location.pathname.includes('/inicio')) {
      navigate.push('/inicio');
    }
  };

  const content = (
    <Box height="100%" display="flex" flexDirection="column">
      <PerfectScrollbar options={{ suppressScrollX: true }}>
        <Box p={2}>
          {user?.type === UserType.CDTEC
            && (user?.role === UserRole.ADMIN || user?.role === UserRole.OPERATOR
              || user?.role === UserRole.AGRO)
            && navConfig.length > 0 && (
              <List>
                {renderNavItems(
                  filterNavItemsByRole(navConfig, user.type as UserType, user.role as UserRole),
                  location.pathname,
                  user.type as UserType,
                )}
              </List>
          )}
          <Divider />
          <Box key='account-container' width='100%' mt={2} mb={2}>
            {localAccounts && localAccounts.length > 0 && selectedAccount ? (
              <Autocomplete
                key='account-item'
                options={localAccounts}
                getOptionLabel={(option) => option.name}
                value={selectedAccount}
                onChange={onAccountChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label='Cuenta'
                    variant='standard'
                    fullWidth
                  />
                )}
                disableClearable
                disabled={localAccounts.length === 1}
                isOptionEqualToValue={(option, value) => option?.id === value?.id}
              />
            ) : (
              <TextField
                key='account-item'
                label='Cuenta'
                name='selectedAccount'
                variant='standard'
                fullWidth
                value=''
                disabled
              />
            )}
          </Box>
          <Box key='farm-container' width='100%' mt={2} mb={2}>
            <TextField
              value={selectedFarm?.id || ''}
              onChange={onFarmChange}
              variant='standard'
              fullWidth
              select
              label='Campo'
              sx={{ marginBottom: '5px' }}
            >
              {farmsOptions?.map((elem) => (
                <MenuItem key={elem.id} value={elem.id}>
                  {elem.name}
                </MenuItem>
              ))}
            </TextField>
          </Box>
          {selectedAccount?.id && (
            <>
              {isLoadingSectors ? (
                <Box display="flex" justifyContent="center" p={2}>
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <List>
                  {renderNavItems(sectorItems, location.pathname, user?.type as UserType)}
                </List>
              )}
            </>
          )}
          {!selectedAccount?.id && <ListItem disabled>
            Selecciona un cliente
          </ListItem>}
        </Box>
      </PerfectScrollbar>
    </Box>
  );

  return <>
    <Hidden lgUp>
      <Drawer
        anchor="left"
        classes={{ paper: classes.mobileDrawer }}
        onClose={onMobileClose}
        open={openMobile}
        variant="temporary"
      >
        {content}
      </Drawer>
    </Hidden>
    <Hidden lgDown>
      <Drawer
        anchor="left"
        classes={{ paper: classes.desktopDrawer }}
        open={openDrawer}
        variant="persistent"
      >
        {content}
      </Drawer>
      <Box
        onClick={onDrawerAction}
        className={openDrawer ? `${classes.openCloseDrawerBox} ${classes.openDrawerIcon}` : `${classes.openCloseDrawerBox} ${classes.closedDrawerIcon}`}>
        {openDrawer ? <ChevronLeftIcon /> : <ChevronRightIcon />}
      </Box>
    </Hidden>
  </>;
}

NavBar.propTypes = {
  onMobileClose: PropTypes.func,
  openMobile: PropTypes.bool,
  onDrawerAction: PropTypes.func,
  openDrawer: PropTypes.bool,
};

export default NavBar;
